import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import TabContext from '@mui/lab/TabContext';
import Box from '@mui/system/Box';
import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import { useAppDispatch } from '../../redux/hooks';
import { fetchOrdersByMadkholOrderId, sendOrderToOrderQueue } from '../../redux/modules/alpaca/alpaca.actions';
import { alpacaOrderByRequestIdSelector, alpacaSelector } from '../../redux/modules/alpaca/alpaca.selectors';
import { ReducerState } from '../../redux/store.types';
import { CARD_BORDER } from '../../components/Section';
import { isProduction } from '../../constants/environment';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { AlpacaDetails } from './components/alpaca';

interface AlpacaTabProps {
  orderId: string;
}

export function AlpacaTab({ orderId }: AlpacaTabProps) {
  const dispatch = useAppDispatch();
  const { error, sendOrderToOrderQueueStatus } = useSelector(alpacaSelector);
  const { orders } = useSelector((state: ReducerState) => alpacaOrderByRequestIdSelector(state, 'by-madkhol-order-id'));
  const [selectedTab, setSelectedTab] = React.useState<string>('0');
  const [createMockError, setCreateMockError] = React.useState<string | null>(null);

  const fetchOrders = useCallback(() => {
    dispatch(fetchOrdersByMadkholOrderId(orderId));
  }, [orderId, dispatch]);

  const handleCreateMockOrders = useCallback(() => {
    if (!orderId) return;
    setCreateMockError(null);
    dispatch(
      sendOrderToOrderQueue({
        madkholOrderId: orderId,
        useMock: true,
      }),
    )
      .unwrap()
      .then(() => {
        fetchOrders();
      })
      .catch((err: { message?: string }) => {
        setCreateMockError(err?.message ?? 'Failed to create mock orders');
      });
  }, [orderId, dispatch, fetchOrders]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };

  useEffect(() => {
    if (orderId) {
      fetchOrders();
    }
  }, []);

  const renderEmptyState = () => {
    if (isProduction || !orderId || orders.length > 0) return null;
    return (
      <Box sx={{ py: 2, px: 1 }}>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          No Alpaca orders yet. If Alpaca rejected the order (e.g. T+2), you can create mock orders to continue.
        </Typography>
        {createMockError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {createMockError}
          </Alert>
        )}
        <Button
          variant="contained"
          onClick={handleCreateMockOrders}
          disabled={sendOrderToOrderQueueStatus === fetchStatuses.pending}
        >
          {sendOrderToOrderQueueStatus === fetchStatuses.pending ? 'Creating…' : 'Create mock orders'}
        </Button>
      </Box>
    );
  };

  const renderOrders = () => {
    if (!orders.length) {
      return null;
    }

    return (
      <TabContext value={selectedTab}>
        <Box sx={{ width: '100%', borderBottom: `1px solid ${CARD_BORDER}`, mb: '8px' }}>
          <TabList
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons
            allowScrollButtonsMobile
            aria-label="alpaca fund orders"
          >
            {orders.map((order, index) => (
              <Tab
                key={`alpaca-fund-order-${order.id}`}
                value={index.toString()}
                label={`Fund Order #${order.fundOrderId}`}
                sx={{ textTransform: 'none', fontWeight: 600, minHeight: '44px' }}
                disableRipple
              />
            ))}
          </TabList>
        </Box>
        {orders.map((order, index) => (
          <TabPanel key={`alpaca-fund-order-panel-${order.id}`} value={index.toString()} sx={{ width: '100%', p: 0 }}>
            <AlpacaDetails order={order} refresh={fetchOrders} />
          </TabPanel>
        ))}
      </TabContext>
    );
  };

  return (
    <>
      {error && <Alert severity="error">{error.message}</Alert>}
      {renderEmptyState()}
      {renderOrders()}
    </>
  );
}
